import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type SupplierFilterState = {
  searchTerm: string;
  hasChatwoot: boolean | null; // null = todos, true = vinculado, false = não vinculado
  hasTelefone: boolean | null; // null = todos, true = com telefone, false = sem telefone
  hasEmail: boolean | null; // null = todos, true = com email, false = sem email
  hasContato: boolean | null; // null = todos, true = com contato, false = sem contato
  hasWhatsapp: boolean | null; // null = todos, true = com whatsapp, false = sem whatsapp
};

type SupplierFiltersProps = {
  filters: SupplierFilterState;
  onFiltersChange: (filters: SupplierFilterState) => void;
  hasToken?: boolean;
  counts?: {
    hasChatwoot: {
      vinculado: number;
      naoVinculado: number;
    };
    hasTelefone: {
      com: number;
      sem: number;
    };
    hasEmail: {
      com: number;
      sem: number;
    };
    hasContato: {
      com: number;
      sem: number;
    };
    hasWhatsapp: {
      com: number;
      sem: number;
    };
  };
};

type TabType = "resumo" | "chatwoot" | "contato";

export function SupplierFilters({
  filters,
  onFiltersChange,
  hasToken = false,
  counts,
}: SupplierFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");

  const filterCount = 
    (hasToken && filters.hasChatwoot !== null ? 1 : 0) +
    (filters.hasTelefone !== null ? 1 : 0) +
    (filters.hasEmail !== null ? 1 : 0) +
    (filters.hasContato !== null ? 1 : 0) +
    (filters.hasWhatsapp !== null ? 1 : 0);

  const clearAllFilters = () => {
    onFiltersChange({
      searchTerm: filters.searchTerm,
      hasChatwoot: hasToken ? null : filters.hasChatwoot,
      hasTelefone: null,
      hasEmail: null,
      hasContato: null,
      hasWhatsapp: null,
    });
  };

  const removeFilter = (type: keyof SupplierFilterState) => {
    if (type === "hasChatwoot") {
      onFiltersChange({ ...filters, hasChatwoot: null });
    } else if (type === "hasTelefone") {
      onFiltersChange({ ...filters, hasTelefone: null });
    } else if (type === "hasEmail") {
      onFiltersChange({ ...filters, hasEmail: null });
    } else if (type === "hasContato") {
      onFiltersChange({ ...filters, hasContato: null });
    } else if (type === "hasWhatsapp") {
      onFiltersChange({ ...filters, hasWhatsapp: null });
    }
  };

  const tabs: Array<{ id: TabType; label: string; icon: string }> = [
    { id: "resumo", label: "Resumo", icon: "📋" },
    { id: "contato", label: "Contato", icon: "📞" },
    ...(hasToken ? [{ id: "chatwoot" as TabType, label: "Chatwoot", icon: "💬" }] : []),
  ];

  const getTabCount = (tabId: TabType) => {
    switch (tabId) {
      case "chatwoot": return filters.hasChatwoot !== null ? 1 : 0;
      case "contato": 
        return (filters.hasTelefone !== null ? 1 : 0) +
               (filters.hasEmail !== null ? 1 : 0) +
               (filters.hasContato !== null ? 1 : 0) +
               (filters.hasWhatsapp !== null ? 1 : 0);
      default: return 0;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
                {filterCount > 0 && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
                    {filterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Filtre a listagem de fornecedores</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[400px] p-0" align="start">
        <div className="flex h-[350px]">
          {/* Sidebar */}
          <div className="w-48 border-r bg-muted/30 p-2 overflow-y-auto">
            {tabs.map((tab) => {
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </span>
                  {count > 0 && (
                    <Badge variant={activeTab === tab.id ? "secondary" : "default"} className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 p-4 overflow-y-auto">
            {activeTab === "resumo" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Resumo dos Filtros</h3>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                      Limpar tudo
                    </Button>
                  )}
                </div>

                {filterCount === 0 ? (
                  <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                ) : (
                  <div className="space-y-3">
                    {filters.hasTelefone !== null && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Telefone</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.hasTelefone ? "Com telefone" : "Sem telefone"}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("hasTelefone")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                    {filters.hasEmail !== null && (
                      <div>
                        <Label className="text-xs text-muted-foreground">E-mail</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.hasEmail ? "Com e-mail" : "Sem e-mail"}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("hasEmail")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                    {filters.hasContato !== null && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Contato</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.hasContato ? "Com contato" : "Sem contato"}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("hasContato")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                    {filters.hasWhatsapp !== null && (
                      <div>
                        <Label className="text-xs text-muted-foreground">WhatsApp</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.hasWhatsapp ? "Com WhatsApp" : "Sem WhatsApp"}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("hasWhatsapp")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                    {hasToken && filters.hasChatwoot !== null && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Chatwoot</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.hasChatwoot ? "Vinculado" : "Não vinculado"}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("hasChatwoot")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "contato" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Informações de Contato</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Telefone</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="telefone-com"
                          checked={filters.hasTelefone === true}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasTelefone: checked ? true : null,
                            });
                          }}
                        />
                        <Label htmlFor="telefone-com" className="flex-1 flex items-center gap-2">
                          Com telefone
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasTelefone?.com ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="telefone-sem"
                          checked={filters.hasTelefone === false}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasTelefone: checked ? false : null,
                            });
                          }}
                        />
                        <Label htmlFor="telefone-sem" className="flex-1 flex items-center gap-2">
                          Sem telefone
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasTelefone?.sem ?? 0}
                          </Badge>
                        </Label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">E-mail</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="email-com"
                          checked={filters.hasEmail === true}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasEmail: checked ? true : null,
                            });
                          }}
                        />
                        <Label htmlFor="email-com" className="flex-1 flex items-center gap-2">
                          Com e-mail
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasEmail?.com ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="email-sem"
                          checked={filters.hasEmail === false}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasEmail: checked ? false : null,
                            });
                          }}
                        />
                        <Label htmlFor="email-sem" className="flex-1 flex items-center gap-2">
                          Sem e-mail
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasEmail?.sem ?? 0}
                          </Badge>
                        </Label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">Nome do Contato</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="contato-com"
                          checked={filters.hasContato === true}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasContato: checked ? true : null,
                            });
                          }}
                        />
                        <Label htmlFor="contato-com" className="flex-1 flex items-center gap-2">
                          Com contato
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasContato?.com ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="contato-sem"
                          checked={filters.hasContato === false}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasContato: checked ? false : null,
                            });
                          }}
                        />
                        <Label htmlFor="contato-sem" className="flex-1 flex items-center gap-2">
                          Sem contato
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasContato?.sem ?? 0}
                          </Badge>
                        </Label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">WhatsApp</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="whatsapp-com"
                          checked={filters.hasWhatsapp === true}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasWhatsapp: checked ? true : null,
                            });
                          }}
                        />
                        <Label htmlFor="whatsapp-com" className="flex-1 flex items-center gap-2">
                          Com WhatsApp
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasWhatsapp?.com ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="whatsapp-sem"
                          checked={filters.hasWhatsapp === false}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasWhatsapp: checked ? false : null,
                            });
                          }}
                        />
                        <Label htmlFor="whatsapp-sem" className="flex-1 flex items-center gap-2">
                          Sem WhatsApp
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasWhatsapp?.sem ?? 0}
                          </Badge>
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "chatwoot" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status Chatwoot</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="chatwoot-vinculado"
                      checked={filters.hasChatwoot === true}
                      onCheckedChange={(checked) => {
                        onFiltersChange({
                          ...filters,
                          hasChatwoot: checked ? true : null,
                        });
                      }}
                    />
                    <Label htmlFor="chatwoot-vinculado" className="flex-1 flex items-center gap-2">
                      Vinculado
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                        {counts?.hasChatwoot?.vinculado ?? 0}
                      </Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="chatwoot-nao-vinculado"
                      checked={filters.hasChatwoot === false}
                      onCheckedChange={(checked) => {
                        onFiltersChange({
                          ...filters,
                          hasChatwoot: checked ? false : null,
                        });
                      }}
                    />
                    <Label htmlFor="chatwoot-nao-vinculado" className="flex-1 flex items-center gap-2">
                      Não vinculado
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                        {counts?.hasChatwoot?.naoVinculado ?? 0}
                      </Badge>
                    </Label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

